import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useTenant } from "@/contexts/TenantContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "@/components/ui/sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { logCrud } from "@/services/activities";
import { Label } from "@/components/ui/label";

export default function KnowledgeBaseDocuments() {
  const { tenantId } = useTenant();
  const [kbList, setKbList] = useState<Array<{ id: string; nome: string }>>([]);
  const [kbId, setKbId] = useState<string>("");
  const [docs, setDocs] = useState<Array<{ id: string; filename: string; mime_type: string; source_url: string }>>([]);
  const [file, setFile] = useState<File | null>(null);
  const [n8nUrlFiles, setN8nUrlFiles] = useState<string>("");
  const [progress, setProgress] = useState<Record<string, { status: string; indexedCount: number }>>({});
  const [driveFileId, setDriveFileId] = useState<string>("");
  const [savingWebhook, setSavingWebhook] = useState(false);
  const canUse = useMemo(() => !!tenantId, [tenantId]);

  useEffect(() => {
    const presetFiles = (import.meta as any).env?.VITE_N8N_RAG_FILES_URL as string | undefined;
    const key = tenantId ? `rag:n8n:files:${tenantId}` : null;
    const stored = key ? localStorage.getItem(key) : null;
    if (stored) setN8nUrlFiles(stored);
    else if (presetFiles) setN8nUrlFiles(presetFiles);
  }, [tenantId]);

  useEffect(() => {
    const loadKb = async () => {
      if (!tenantId) return;
      const { data } = await supabase.from("knowledge_bases" as any).select("id,nome").eq("tenant_id", tenantId).eq("status", "ativo");
      setKbList((data as any[]) || []);
    };
    loadKb();
  }, [tenantId]);

  useEffect(() => {
    const loadDocs = async () => {
      if (!tenantId || !kbId) return;
      const { data } = await supabase.from("kb_documents" as any).select("id,filename,mime_type,source_url").eq("tenant_id", tenantId).eq("kb_id", kbId).eq("status", "ativo");
      setDocs((data as any[]) || []);
    };
    loadDocs();
  }, [tenantId, kbId]);

  const upload = async () => {
    if (!tenantId || !kbId || !file) {
      toast("Selecione base e arquivo");
      return;
    }
    const path = `${tenantId}/${kbId}/${Date.now()}_${file.name}`;
    const { error } = await supabase.storage.from("kb-documents" as any).upload(path, file);
    if (error) {
      toast("Erro ao enviar arquivo");
      return;
    }
    const { data: insData, error: insErr } = await supabase.from("kb_documents" as any).insert({ tenant_id: tenantId, kb_id: kbId, source_url: path, filename: file.name, mime_type: file.type, status: "ativo" }).select("id").single();
    if (insErr) {
      toast("Erro ao salvar documento");
      return;
    }
    if (insData?.id) {
      void logCrud({ entitySlug: "entity:kb_documents", operationSlug: "create", entityId: insData.id, entityName: file.name, screenSlug: "configuracoes/rag-documentos" });
    }
    const { data } = await supabase.from("kb_documents" as any).select("id,filename,mime_type,source_url").eq("tenant_id", tenantId).eq("kb_id", kbId).eq("status", "ativo");
    setDocs((data as any[]) || []);
    setFile(null);
    toast("Documento salvo");
  };

  const indexDoc = async (doc: { id: string; source_url: string; filename: string; mime_type: string }) => {
    if (!tenantId || !kbId || !n8nUrlFiles) {
      toast("Configure o webhook do n8n e selecione a base");
      return;
    }
    const signed = await supabase.storage.from("kb-documents" as any).createSignedUrl(doc.source_url, 3600);
    if (!signed?.data?.signedUrl) {
      toast("Erro ao gerar URL assinada");
      return;
    }
    const payload = { file_url: signed.data.signedUrl, tenant_id: tenantId, kb_id: kbId, filename: doc.filename, mime_type: doc.mime_type } as any;
    const res = await fetch(n8nUrlFiles, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!res.ok) {
      toast("Erro ao acionar ingestão n8n");
      return;
    }
    toast("Ingestão acionada");

    setProgress((prev) => ({ ...prev, [doc.id]: { status: "indexando", indexedCount: 0 } }));
    const interval = setInterval(async () => {
      const { data } = await supabase
        .from("ai_search_index" as any)
        .select("id, created_at, metadata")
        .eq("tenant_id", tenantId)
        .eq("entity_type", "kb_chunk")
        .contains("metadata", { kb_id: kbId, filename: doc.filename }) as any;
      const count = (data as any[])?.length || 0;
      setProgress((prev) => ({ ...prev, [doc.id]: { status: count > 0 ? "indexado" : "indexando", indexedCount: count } }));
      if (count > 0) clearInterval(interval);
    }, 3000);
  };

  const indexDriveFile = async () => {
    if (!tenantId || !kbId || !driveFileId || !n8nUrlFiles) {
      toast("Preencha base, fileId e webhook n8n");
      return;
    }
    const payload = { file_id: driveFileId, tenant_id: tenantId, kb_id: kbId } as any;
    const driveWebhook = (import.meta as any).env?.VITE_N8N_RAG_DRIVE_URL || "";
    const res = await fetch(driveWebhook, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(payload) });
    if (!res.ok) {
      toast("Erro ao acionar ingestão do Drive");
      return;
    }
    toast("Ingestão do Drive acionada");
  };

  if (!canUse) {
    return (
      <div className="p-6">
        <div className="text-center space-y-2">
          <div className="text-2xl font-bold">Acesso Negado</div>
          <div className="text-muted-foreground">Nenhum tenant selecionado</div>
        </div>
      </div>
    );
  }

  const saveWebhook = () => {
    if (!tenantId) return;
    setSavingWebhook(true);
    try {
      localStorage.setItem(`rag:n8n:files:${tenantId}`, n8nUrlFiles || "");
      toast("Webhook salvo para o tenant");
    } finally {
      setSavingWebhook(false);
    }
  };

  return (
    <div className="p-6">
      <Card>
        <CardHeader>
          <CardTitle>RAG: Documentos da Base</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-2 max-w-xl">
            <Label>Base de Conhecimento</Label>
            <Select value={kbId} onValueChange={(v) => setKbId(v)}>
              <SelectTrigger>
                <SelectValue placeholder="Selecionar Base de Conhecimento" />
              </SelectTrigger>
              <SelectContent>
                {kbList.map((kb) => (
                  <SelectItem key={kb.id} value={kb.id}>{kb.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Label>Webhook n8n (Arquivos)</Label>
            <Input placeholder="https://seu-n8n/webhook/rag-files" value={n8nUrlFiles} onChange={(e) => setN8nUrlFiles(e.target.value)} />
            <div className="flex gap-2">
              <Button variant="outline" onClick={saveWebhook} disabled={savingWebhook || !tenantId}>Salvar Webhook</Button>
            </div>
            <Input type="file" onChange={(e) => setFile(e.target.files?.[0] || null)} />
            <Button onClick={upload} disabled={!kbId || !file}>Salvar Documento</Button>
            <Input placeholder="Google Drive File ID" value={driveFileId} onChange={(e) => setDriveFileId(e.target.value)} />
            <Button variant="secondary" onClick={indexDriveFile} disabled={!kbId || !driveFileId || !n8nUrlFiles}>Indexar do Drive</Button>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>MIME</TableHead>
                <TableHead>Progresso</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {docs.map((d) => (
                <TableRow key={d.id}>
                  <TableCell>{d.filename}</TableCell>
                  <TableCell>{d.mime_type}</TableCell>
                  <TableCell>{progress[d.id]?.status || "-"} {progress[d.id]?.indexedCount ? `(${progress[d.id]?.indexedCount})` : ""}</TableCell>
                  <TableCell>
                    <Button variant="secondary" onClick={() => indexDoc(d)} disabled={!n8nUrlFiles}>Indexar</Button>
                  </TableCell>
                </TableRow>
              ))}
              {docs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} className="text-sm text-muted-foreground">Nenhum documento carregado para a base selecionada</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
